import React, {useEffect, useState} from "react";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {useSearchParams} from "react-router-dom";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {VsiBubble, VsiH4} from "../../../styled/App.styled";
import Grid from "../../../components/Grid";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import VerticalBarChart from "../../../components/VerticalBarChart";
import {integerFormatter, numberFormatter} from "../../../utils/gridFormatters";
import {Card} from "reactstrap";

const defs = [
    {
        "field": "array_name",
        "headerName": "Array Name",
        "enableRowGroup": true
    },
    {
        "field": "cluster_name",
        "headerName": "Cluster Name",
        "enableRowGroup": true
    },
    {
        "field": "volume_name",
        "headerName": "Volume Name",
        "enableRowGroup": true
    },
    {
        "field": "source_id",
        "headerName": "Source ID",
        "enableRowGroup": true
    },
    {
        "field": "target_array_name",
        "headerName": "Target Array Name",
        "enableRowGroup": true
    },
    {
        "field": "target_volume_name",
        "headerName": "Target Volume Name",
        "enableRowGroup": true
    },
    {
        "field": "target_id",
        "headerName": "Target ID",
        "enableRowGroup": true
    },
    {
        "field": "local_remote",
        "headerName": "Local Remote",
        "enableRowGroup": true
    },
    {
        "field": "latest_timestamp",
        "headerName": "Timestamp",
        "filter": "agDateColumnFilter",
        "filterParams": {
            "cellHeight": 20,
            "debounceMs": 1000
        }
    },
    {
        "field": "replication_status",
        "headerName": "Status",
        "enableRowGroup": true
    },
    {
        "field": "device_type",
        "headerName": "Device Type"
    },
    {
        "field": "snap_count",
        "type": "numericColumn",
        "headerName": "Snap Count",
        "cellRenderer": integerFormatter,
        "filter": "agNumberColumnFilter",
        "filterParams": {
            "cellHeight": 20,
            "debounceMs": 1000
        },
        "enableRowGroup": true
    },
    {
        "field": "snap_used",
        "type": "numericColumn",
        "headerName": "Snap used",
        "filter": "agNumberColumnFilter",
        "filterParams": {
            "cellHeight": 20,
            "debounceMs": 1000
        },
        "enableRowGroup": true,
        "cellRenderer": numberFormatter
    },
    {
        "field": "classification",
        "headerName": "Classification",
        "hide": true,
        "enableRowGroup": true
    },
    {
        "field": "data_center",
        "headerName": "Data Center",
        "hide": true,
        "enableRowGroup": true
    }
    ,
    {
        "field": "sync_delay",
        "headerName": "Sync Delay",
        "hide": true
    }
];

const allFilters = [
    {value: 'classification', label: 'Classification'},
    {value: 'device_type', label: 'Device Type'},
    {value: 'data_center', label: 'Data Center'},
];

const sortingArr = ['local', 'remote'];

export default function EnterpriseSnap({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();
    const [allData, setAllData] = useState([]);
    const [allFilCats, setAllFilCats] = useState([]);

    const searchFilter = searchParams.get('filter');
    let defaultFilter = allFilters[0];
    if (searchFilter) {
        defaultFilter = allFilters.find(({value}) => searchFilter === value) || defaultFilter;
    };
    const [filter, setFilter] = useState(defaultFilter);
    const [graphData, setGraphData] = useState({
        categories: [],
        series: []
    });

    // const [data, { isLoading }] = useDataWithRoute(`/api/mock/enterprise_snap`, mock);
    const [data] = useDataWithRoute(`/api/v6/report/enterprise_snap_proc?client_id=${client.clientId}&period_id=${periodId}`);

    useEffect(() => {
        const validData = data?.recordset || [];
        setAllData(validData);
        updateChartWithSelection(validData);
    }, [data, searchParams]);

    const updateChartWithSelection = (data, selFilter) => {
        var localArr = [];
        var remoteArr = [];

        data.forEach(datum => {
            if (datum.local_remote === "local") {
                localArr.push(datum)
            } else if (datum.local_remote === "remote") {
                remoteArr.push(datum)
            };
        });

        var allCats = [];
        var activeFil = selFilter ? selFilter : filter;
        if (activeFil.value === "device_type") {
            var deviceTypes = data.map(datum => {
                return datum.device_type
            });
            allCats = deviceTypes.filter((deviceType, index) => {
                return deviceTypes.indexOf(deviceType) === index;
            });
            setAllFilCats(allCats);

            // Local only
            let localNewData = [];
            for (let i = 0; i < allCats.length; i++) {
                let snapUsed = 0;
                for (let y = 0; y < localArr.length; y++) {
                    if (allCats[i] === localArr[y].device_type) {
                        snapUsed = snapUsed + localArr[y].snap_used;
                    };
                };
                localNewData.push(parseFloat((snapUsed).toFixed(2)));
            };

            // Remote only
            let remoteNewData = [];
            for (let i = 0; i < allCats.length; i++) {
                let snapUsed = 0;
                for (let y = 0; y < remoteArr.length; y++) {
                    if (allCats[i] === remoteArr[y].device_type) {
                        snapUsed = snapUsed + remoteArr[y].snap_used;
                    };
                };
                remoteNewData.push(parseFloat((snapUsed).toFixed(2)));
            };
            var finalArray = [localNewData, remoteNewData];

            var chartInfo = [];
            for (let i = 0; i < sortingArr.length; i++) {
                chartInfo.push({
                    name: sortingArr[i],
                    data: finalArray[i]
                });
            };
            setGraphData(chartInfo);
        } else if (activeFil.value === "data_center") {
            var dataCenters = data.map(datum => {
                return datum.data_center
            });
            allCats = dataCenters.filter((dataCenter, index) => {
                return dataCenters.indexOf(dataCenter) === index;
            });
            setAllFilCats(allCats);

            // Local only
            let localNewData = [];
            for (let i = 0; i < allCats.length; i++) {
                let snapUsed = 0;
                for (let y = 0; y < localArr.length; y++) {
                    if (allCats[i] === localArr[y].data_center) {
                        snapUsed = snapUsed + localArr[y].snap_used;
                    };
                };
                localNewData.push(parseFloat((snapUsed).toFixed(2)));
            };

            // Remote only
            let remoteNewData = [];
            for (let i = 0; i < allCats.length; i++) {
                let snapUsed = 0;
                for (let y = 0; y < remoteArr.length; y++) {
                    if (allCats[i] === remoteArr[y].data_center) {
                        snapUsed = snapUsed + remoteArr[y].snap_used;
                    };
                };
                remoteNewData.push(parseFloat((snapUsed).toFixed(2)));
            };
            finalArray = [localNewData, remoteNewData];

            chartInfo = [];
            for (let i = 0; i < sortingArr.length; i++) {
                chartInfo.push({
                    name: sortingArr[i],
                    data: finalArray[i]
                });
            };
            setGraphData(chartInfo);
        } else {
            // default to classification
            var classifications = data.map(datum => {
                return datum.classification
            });
            allCats = classifications.filter((classType, index) => {
                return classifications.indexOf(classType) === index;
            });
            setAllFilCats(allCats);

            // Local only
            let localNewData = [];
            for (let i = 0; i < allCats.length; i++) {
                let snapUsed = 0;
                for (let y = 0; y < localArr.length; y++) {
                    if (allCats[i] === localArr[y].classification) {
                        snapUsed = snapUsed + localArr[y].snap_used;
                    };
                };
                localNewData.push(parseFloat((snapUsed).toFixed(2)));
            };

            // Remote only
            let remoteNewData = [];
            for (let i = 0; i < allCats.length; i++) {
                let snapUsed = 0;
                for (let y = 0; y < remoteArr.length; y++) {
                    if (allCats[i] === remoteArr[y].classification) {
                        snapUsed = snapUsed + remoteArr[y].snap_used;
                    };
                };

                remoteNewData.push(parseFloat((snapUsed).toFixed(2)));
            };

            finalArray = [localNewData, remoteNewData];

            chartInfo = [];
            for (let i = 0; i < sortingArr.length; i++) {
                chartInfo.push({
                    name: sortingArr[i],
                    data: finalArray[i]
                });
            };
            setGraphData(chartInfo);
        };

    };

    const onFilterChange = (selectedFilter) => {
        setFilter(selectedFilter);
        setSearchParams({
            filter: selectedFilter.value,
        }, {replace: true});
        updateChartWithSelection(data?.[0]?.dataGrid || [], selectedFilter);
    };

    const gridFilterChanged = (eventData) => {
        let filteredData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if(node.data) filteredData.push(node.data);
        });

        updateChartWithSelection(filteredData);
    };

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Enterprise Snap / Replications</VsiH4>
        <div className={"w-100 p-2 d-flex"}>
            <VsiLabeledSelect options={allFilters} onChange={onFilterChange} defaultValue={filter}/>
        </div>
        <VsiBubble className={"mb-2"} unit={filter.value}>
            <VerticalBarChart
                categories={allFilCats}
                height={600}
                includeLegend={true}
                series={graphData}
                yTitle1={"GiB"}
            />
        </VsiBubble>
        <VsiBubble unit={filter.value}>
            <Grid data={allData} saveParams={{unit: filter.value}} columnDefs={defs} filterChanged={gridFilterChanged}/>
        </VsiBubble>
    </Card>
}
